import {
    ClientSDK,
    ForgotPasswordRequestBody,
    LoginRequestBody,
    LoginResponse,
    RegistrationCreateRequestBody,
    ResetPasswordBody,
    User,
} from '@easeltv/client-sdk';
import { ok, Result } from '@easeltv/fail';
import { useLocalStorage } from '@easeltv/hooks';
import { ApiFail, RequestResult } from '@easeltv/remote';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Portal } from '../../components/Modal';
import SignOutModal from '../../components/Modal/SignOutModal';
import { useApi } from '../../hooks/useApi/useApi';

/* ---------------------------------- Types --------------------------------- */

export interface AuthContextI {
    cancelSubscription: typeof ClientSDK.prototype.cancelSubscription;
    completeRegistration: typeof ClientSDK.prototype.completeRegistration;
    detachObserver: (observer: AuthObserverFn) => void;
    fetchUser: typeof ClientSDK.prototype.getCurrentUser;
    forgotPassword: typeof ClientSDK.prototype.forgotPassword;
    isAuthenticated: boolean;
    login: (
        body: LoginRequestBody,
    ) => Promise<Result<LoginResponse & User, ApiFail>>;
    logout: () => void;
    registerObserver: (observer: AuthObserverFn) => void;
    resetPassword: typeof ClientSDK.prototype.resetPassword;
    startRegistration: typeof ClientSDK.prototype.startRegistration;
    user: User | null;
}

/* --------------------------------- Context -------------------------------- */

export const AuthContext = createContext<AuthContextI | null>(null);

/* ---------------------------------- Hook ---------------------------------- */

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        // This is a developer error that should never come to pass
        // But for the sake of TS we have to account for it
        // https://kentcdodds.com/blog/how-to-use-react-context-effectively#the-custom-consumer-hook
        throw Error('Cannot use AuthContext outside the AuthProvider');
    }

    return context;
};

const useGetUser = () => {
    const api = useApi();

    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [called, setCalled] = useState(false);

    const observers = useRef<AuthObserverFn[]>([]);

    const registerObserver = useCallback((observer: AuthObserverFn) => {
        const doesExist = observers.current.includes(observer);

        if (!doesExist) {
            observers.current = [...observers.current, observer];
        }
    }, []);

    const detachObserver = useCallback((observer: AuthObserverFn) => {
        const observerIndex = observers.current.indexOf(observer);
        if (observerIndex > -1) {
            const newObservers = [...observers.current];

            newObservers.splice(observerIndex, 1);

            observers.current = newObservers;
        }
    }, []);

    const notifyObservers: AuthObserverFn = useCallback((updatedUser) => {
        observers.current.forEach((observer) => observer(updatedUser));
    }, []);

    const fetchUser = useCallback(async () => {
        setLoading(true);

        const result = await api.getCurrentUser();

        notifyObservers(result);

        if (result.ok) {
            setUser(result.payload.body);
        } else {
            setUser(null);
        }

        setLoading(false);
        setCalled(true);

        return result;
    }, [api, notifyObservers]);

    return [
        fetchUser,
        {
            called,
            detachObserver,
            loading,
            registerObserver,
            user,
        },
    ] as const;
};

/* -------------------------------- Provider -------------------------------- */

type AuthObserverFn = (updatedUser: RequestResult<User>) => void;

const AuthProvider = ({ children }: PropsWithChildren) => {
    const api = useApi();
    const [fetchUser, { user, called, registerObserver, detachObserver }] =
        useGetUser();
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    const isAuthenticated = useMemo(() => !!user, [user]);

    const [, setRefreshToken] = useLocalStorage<string | undefined>(
        'refreshToken',
        undefined,
    );

    const login = useCallback(
        async (body: LoginRequestBody) => {
            const loginResult = await api.login(body);

            if (!loginResult.ok) {
                return loginResult;
            }

            const userResult = await fetchUser();

            if (!userResult.ok) {
                return userResult;
            }

            setRefreshToken(loginResult.payload.body.refreshToken);

            return ok({
                ...loginResult.payload.body,
                ...userResult.payload.body,
            });
        },
        [api, setRefreshToken, fetchUser],
    );

    const logout = useCallback(() => setShowModal(true), []);

    const logoutFn = useCallback(async () => {
        const result = await api.logout();

        if (!result.ok) {
            return result;
        }

        setRefreshToken(undefined);

        await fetchUser();

        navigate('/');

        return result;
    }, [api, navigate, setRefreshToken, fetchUser]);

    const startRegistration = useCallback(
        async (body: RegistrationCreateRequestBody) => {
            const result = await api.startRegistration(body);

            if (!result.ok) {
                return result;
            }

            await fetchUser();

            return result;
        },
        [api, fetchUser],
    );

    const completeRegistration = useCallback(
        async (id: string) => {
            const result = await api.completeRegistration(id);

            if (!result.ok) {
                return result;
            }

            setRefreshToken(result.payload.body.refreshToken);

            await fetchUser();

            window.analytics.trigger({
                type: 'account.registration.completed',
                userId: result.payload.body.userId,
            });

            return result;
        },
        [api, setRefreshToken, fetchUser],
    );

    const forgotPassword = useCallback(
        async (body: ForgotPasswordRequestBody) => {
            const result = await api.forgotPassword(body);

            if (!result.ok) {
                return result;
            }

            await fetchUser();

            return result;
        },
        [api, fetchUser],
    );

    const resetPassword = useCallback(
        async (body: ResetPasswordBody) => {
            const result = await api.resetPassword(body);

            if (!result.ok) {
                return result;
            }

            await fetchUser();

            return result;
        },
        [api, fetchUser],
    );

    const cancelSubscription = useCallback(
        async (id: string) => {
            const result = await api.cancelSubscription(id);

            if (!result.ok) {
                return result;
            }

            await fetchUser();

            return result;
        },
        [api, fetchUser],
    );

    const value = useMemo(
        () => ({
            cancelSubscription,
            completeRegistration,
            detachObserver,
            fetchUser,
            forgotPassword,
            isAuthenticated,
            login,
            logout,
            registerObserver,
            resetPassword,
            startRegistration,
            user,
        }),
        [
            cancelSubscription,
            completeRegistration,
            detachObserver,
            fetchUser,
            forgotPassword,
            isAuthenticated,
            login,
            logout,
            registerObserver,
            resetPassword,
            startRegistration,
            user,
        ],
    );

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    if (!called) {
        return null;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
            <Portal initialShowModal={showModal} onShowChange={setShowModal}>
                {({ Modal, closeModal }) => (
                    <Modal>
                        <SignOutModal
                            closeModal={closeModal}
                            logoutFn={logoutFn}
                        />
                    </Modal>
                )}
            </Portal>
        </AuthContext.Provider>
    );
};

/* --------------------------------- Export --------------------------------- */

export default AuthProvider;
